import Lightbox from '@/components/Lightbox';
import { PhotoAnnotatorModal } from '@/components_v2/PhotoAnnotator';
import { formularyStorage } from '@/modules/shared/adapters/CloudStorage';
import { blobToFile } from '@/utils/blobToFile';
import { readFile } from '@/utils/readFile';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';

import ImagePreview from './components/ImagePreview';
import WebcamCapture from './components/WebcamCapture';
import { ImageCaptureProps } from './interfaces';

export const ImageCapture: React.FC<ImageCaptureProps> = (props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onChange, setUrls, urls, disabled } = props;
  const [pendingPhoto, setPendingPhoto] = React.useState<string | null>(null);

  const modalSize = useBreakpointValue({ base: 'full', lg: '2xl' });

  const captureImageFromWebcam = useCallback((imageSrc: string) => {
    if (imageSrc) {
      setPendingPhoto(imageSrc);
    }
  }, []);

  const handleAnnotatorConfirm = useCallback(
    async (dataUrl: string | null) => {
      const sourceDataUrl = dataUrl ?? pendingPhoto;
      if (!sourceDataUrl) {
        setPendingPhoto(null);
        return;
      }

      const blob = await readFile(sourceDataUrl);
      const file = await blobToFile(blob, `photo-${uuidV4()}.jpg`);

      const remoteUrl = await new Promise<string>((resolve, reject) => {
        formularyStorage
          .uploadFile({ file, setFileDownloadUrl: resolve })
          .catch(reject);
      });

      setUrls((prev) => {
        const newUrls = [...prev, remoteUrl];
        onChange(newUrls);
        return newUrls;
      });
      setPendingPhoto(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, pendingPhoto],
  );

  const handleAnnotatorClose = useCallback(() => {
    setPendingPhoto(null);
  }, []);

  const onRemove = React.useCallback(
    (index: number) => {
      setUrls((prev) => {
        const newUrls = prev
          .slice(0, index)
          .concat(prev.slice(index + 1, prev.length));
        onChange(newUrls);
        return newUrls;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange],
  );

  const onLightbox = React.useCallback(
    (index: number) => {
      Lightbox.openLightbox(urls, index);
    },
    [urls],
  );

  return (
    <>
      <Button mr={2} colorScheme="brand" onClick={onOpen}>
        Tirar foto
      </Button>

      <Modal size={modalSize} isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Capture imagens com a webcam</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack>
              <WebcamCapture
                onCapture={captureImageFromWebcam}
                disabled={disabled}
              />
              {disabled && (
                <Text color="red.500">
                  Você atingiu o limite de imagens para envio.
                </Text>
              )}
              <Text>Imagens na nuvem:</Text>
              <Wrap w="full">
                {urls.map((p, index) => (
                  <WrapItem key={`capture_img_${index}`}>
                    <ImagePreview
                      url={p}
                      onRemove={() => onRemove(index)}
                      onOpen={() => onLightbox(index)}
                    />
                  </WrapItem>
                ))}
              </Wrap>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <PhotoAnnotatorModal
        isOpen={!!pendingPhoto}
        photoSrc={pendingPhoto}
        onClose={handleAnnotatorClose}
        onConfirm={handleAnnotatorConfirm}
      />
    </>
  );
};

export default ImageCapture;
